import BaseApi from '@core/api/model';

import {
    getInvestorPlanKeys,
    getInvestorSubscriptions,
    samePlanKeys
} from '@packages/rate-plans/utils/billingGroupPlanCompatibility';

import { wait } from '@utils/async';

/**
 * Pre-defined mock investors for use in billing group detail views.
 */
export const MOCK_INVESTORS = [
    { id: 'mock-inv-1', name: 'John Smith', email: 'john.smith@example.com' },
    { id: 'mock-inv-2', name: 'Sarah Johnson', email: 'sarah.johnson@example.com' },
    { id: 'mock-inv-3', name: 'David Williams', email: 'david.williams@example.com' },
    { id: 'mock-inv-4', name: 'Emma Brown', email: 'emma.brown@example.com' },
    { id: 'mock-inv-5', name: 'Michael Taylor', email: 'michael.taylor@example.com' },
    { id: 'mock-inv-6', name: 'Lisa Davies', email: 'lisa.davies@example.com' },
    { id: 'mock-inv-7', name: 'Robert Evans', email: 'robert.evans@example.com' },
    { id: 'mock-inv-8', name: 'Amanda Wilson', email: 'amanda.wilson@example.com' },
    { id: 'mock-inv-9', name: 'James Thomas', email: 'james.thomas@example.com' },
    { id: 'mock-inv-10', name: 'Karen Roberts', email: 'karen.roberts@example.com' },
    { id: 'mock-inv-11', name: 'Peter Lewis', email: 'peter.lewis@example.com' },
    { id: 'mock-inv-12', name: 'Susan Walker', email: 'susan.walker@example.com' }
];

/**
 * The mock scenarios: each defines a billing group with realistic data.
 */
const MOCK_SCENARIOS_VERSION = 2;

const MOCK_SCENARIOS = [
    {
        name: 'Smith Family Office',
        investors_count: 3,
        investor_ids: ['mock-inv-1', 'mock-inv-2', 'mock-inv-3'],
        subscriptions: [
            { type: 'ADVISER', name: 'Standard Adviser Plan', status: 'ACTIVE' },
            { type: 'PLATFORM', name: 'Standard Platform Plan', status: 'ACTIVE' }
        ]
    },
    {
        name: 'Johnson & Partners',
        investors_count: 2,
        investor_ids: ['mock-inv-2', 'mock-inv-4'],
        subscriptions: [
            { type: 'ADVISER', name: 'Premium Adviser Plan', status: 'ACTIVE' },
            { type: 'PLATFORM', name: 'Premium Platform Plan', status: 'ACTIVE' }
        ]
    },
    {
        name: 'Williams Joint Account',
        investors_count: 1,
        investor_ids: ['mock-inv-3'],
        subscriptions: [
            { type: 'ADVISER', name: 'Legacy Adviser Plan (retired)', status: 'DEPRECATED' },
            { type: 'PLATFORM', name: 'Standard Platform Plan', status: 'ACTIVE' }
        ]
    },
    {
        name: 'Brown Family Trust',
        investors_count: 4,
        investor_ids: ['mock-inv-4', 'mock-inv-5', 'mock-inv-6', 'mock-inv-7'],
        subscriptions: [
            { type: 'ADVISER', name: 'Family Adviser Plan', status: 'ACTIVE' },
            { type: 'PLATFORM', name: 'Family Platform Plan', status: 'ACTIVE' }
        ]
    },
    {
        name: 'Taylor Holdings',
        investors_count: 1,
        investor_ids: ['mock-inv-5'],
        subscriptions: [
            { type: 'ADVISER', name: 'Standard Adviser Plan', status: 'ACTIVE' },
            { type: 'PLATFORM', name: 'Legacy Platform Plan', status: 'DEPRECATED' }
        ]
    },
    {
        name: 'Davies Corporate',
        investors_count: 2,
        investor_ids: ['mock-inv-6', 'mock-inv-8'],
        subscriptions: [
            { type: 'ADVISER', name: 'Corporate Adviser Plan', status: 'ACTIVE' },
            { type: 'PLATFORM', name: 'Corporate Platform Plan', status: 'ACTIVE' }
        ]
    },
    {
        name: 'Evans Partnership',
        investors_count: 3,
        investor_ids: ['mock-inv-7', 'mock-inv-9', 'mock-inv-10'],
        subscriptions: [
            { type: 'ADVISER', name: 'Partnership Adviser Plan', status: 'ACTIVE' },
            { type: 'PLATFORM', name: 'Partnership Platform Plan', status: 'DEPRECATED' }
        ]
    },
    {
        name: 'Wilson Family Office',
        investors_count: 5,
        investor_ids: ['mock-inv-8', 'mock-inv-9', 'mock-inv-10', 'mock-inv-11', 'mock-inv-12'],
        subscriptions: [
            { type: 'ADVISER', name: 'Family Office Adviser Plan', status: 'ACTIVE' },
            { type: 'PLATFORM', name: 'Family Office Platform Plan', status: 'ACTIVE' }
        ]
    },
    {
        name: 'Thomas Personal',
        investors_count: 1,
        investor_ids: ['mock-inv-9'],
        subscriptions: [
            { type: 'ADVISER', name: 'Standard Adviser Plan', status: 'ACTIVE' },
            { type: 'PLATFORM', name: 'Standard Platform Plan', status: 'ACTIVE' }
        ]
    },
    {
        name: 'Roberts & Co',
        investors_count: 2,
        investor_ids: ['mock-inv-10', 'mock-inv-11'],
        subscriptions: [
            { type: 'ADVISER', name: 'Standard Adviser Plan', status: 'ACTIVE' },
            { type: 'PLATFORM', name: 'Standard Platform Plan', status: 'ACTIVE' }
        ]
    },
    {
        name: 'Lewis Family',
        investors_count: 1,
        investor_ids: ['mock-inv-11'],
        subscriptions: [
            { type: 'ADVISER', name: 'Legacy Adviser Plan (retired)', status: 'DEPRECATED' },
            { type: 'PLATFORM', name: 'Legacy Platform Plan', status: 'DEPRECATED' }
        ]
    },
    {
        name: 'Walker Trust',
        investors_count: 2,
        investor_ids: ['mock-inv-12', 'mock-inv-1'],
        subscriptions: [
            { type: 'ADVISER', name: 'Trust Adviser Plan', status: 'ACTIVE' },
            { type: 'PLATFORM', name: 'Trust Platform Plan', status: 'ACTIVE' }
        ]
    }
];

export class BillingGroupApi extends BaseApi {
    static resource = 'billing-groups';
    static mockItemCount = 12;
    static mockUseFilterOverrides = true;
    static mockFilterOverrideKeys = ['request_adviser_id'];

    _normaliseMockFilters(filters = {}) {
        const normalised = { ...filters };

        if (normalised.request_adviser_id) {
            normalised.adviser_id = normalised.request_adviser_id;
            delete normalised.request_adviser_id;
        }

        return normalised;
    }

    /**
     * Build a subscription object from a scenario definition.
     * Plan ids are derived from the plan name so that the same named plan
     * (e.g. "Standard Adviser Plan") keeps a stable id across the groups that
     * seed it, which lets plan-compatibility logic compare plans by id.
     * @param {object} sub
     * @param {string} adviserId
     * @returns {object}
     */
    _buildSubscription(sub = {}, adviserId = null) {
        const type = sub.type || 'adviser';
        const nameSlug = String(sub.name || '')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '');
        const planId = `mock-${type.toLowerCase()}-plan-${nameSlug || adviserId || 'unknown'}`;
        const hasEndDate = sub.status === 'DEPRECATED';

        return {
            start_date: '2024-01-01',
            end_date: hasEndDate ? '2025-12-31' : null,
            rate_plan_type: sub.type || 'ADVISER',
            rate_plan: {
                id: planId,
                name: sub.name || 'Standard Plan',
                status: sub.status || 'ACTIVE',
                type: sub.type || 'ADVISER',
                description:
                    sub.status === 'DEPRECATED'
                        ? 'This plan has been retired and is no longer available for new subscriptions.'
                        : 'Active fee plan for this billing group.',
                default: false,
                adviser_id: adviserId
            }
        };
    }

    /**
     * Look up mock investors by IDs and return enriched objects.
     * @param {string[]} investorIds
     * @returns {object[]}
     */
    _buildInvestors(investorIds = []) {
        return MOCK_INVESTORS.filter(inv => investorIds.includes(inv.id)).map(inv => ({
            ...inv,
            avatar: null
        }));
    }

    /**
     * Enrich a billing group with full detail data (investors, subscriptions).
     * Prefers any explicitly stored investor/subscription data over the seeded
     * scenario definitions so that membership and rate plan changes survive
     * round trips through the mocked storage.
     * @param {object} group
     * @returns {object}
     */
    _enrichDetail(group = {}) {
        if (!group || typeof group !== 'object') {
            return group;
        }

        const scenario = MOCK_SCENARIOS[group._scenario_index] || null;
        const adviserId = group.adviser_id || 'default';
        const investorIds =
            Array.isArray(group.investor_ids) && group.investor_ids.length
                ? group.investor_ids
                : scenario
                ? scenario.investor_ids
                : [];

        const subscriptions =
            Array.isArray(group.active_subscriptions) && group.active_subscriptions.length
                ? group.active_subscriptions
                : scenario
                ? scenario.subscriptions.map(sub => this._buildSubscription(sub, adviserId))
                : Array.isArray(group.active_subscriptions)
                ? group.active_subscriptions
                : [];

        return {
            ...group,
            investor_ids: investorIds,
            investor_id: group.investor_id != null ? group.investor_id : investorIds[0] || null,
            investors: this._buildInvestors(investorIds),
            investors_count: typeof group.investors_count === 'number' ? group.investors_count : investorIds.length,
            active_subscriptions: subscriptions
        };
    }

    /**
     * Build mock items from the scenario definitions.
     * @param {object} [overrides={}]
     * @returns {object[]}
     */
    _buildScenarioItems(overrides = {}) {
        const adviserId = overrides.adviser_id || `mock-adviser-${Date.now()}`;

        return MOCK_SCENARIOS.map((scenario, index) => ({
            id: `mock-bg-${index + 1}`,
            name: scenario.name,
            investor_id: scenario.investor_ids[0] || null,
            adviser_id: adviserId,
            investors_count: scenario.investors_count,
            investor_ids: scenario.investor_ids,
            investors: this._buildInvestors(scenario.investor_ids),
            active_subscriptions: scenario.subscriptions.map(sub => this._buildSubscription(sub, adviserId)),
            deleted_at: null,
            _scenario_index: index,
            _seed_version: MOCK_SCENARIOS_VERSION
        }));
    }

    /**
     * Whether the mocked store holds data seeded by the current scenario set.
     * Stale stores (e.g. generated by the mocks debugger before the deprecated
     * rate plan scenarios existed) are re-seeded so the scenarios remain testable.
     * @param {object[]} items
     * @returns {boolean}
     */
    _hasCurrentSeed(items = []) {
        const first = items[0] || null;

        return !!first && first._seed_version === MOCK_SCENARIOS_VERSION;
    }

    /**
     * Resolve the subscriptions that belong to a group, falling back to the
     * seeded scenario definitions when the stored item has none.
     * @param {object} group
     * @returns {object[]}
     */
    _getGroupSubscriptions(group = {}) {
        if (Array.isArray(group.active_subscriptions) && group.active_subscriptions.length) {
            return group.active_subscriptions;
        }

        const scenario = MOCK_SCENARIOS[group._scenario_index] || null;

        if (scenario) {
            return scenario.subscriptions.map(sub => this._buildSubscription(sub, group.adviser_id || 'default'));
        }

        return [];
    }

    /**
     * Derive the sorted plan keys an investor currently has, based on every
     * non-deleted mock group they belong to.
     * @param {string} investorId
     * @param {object[]} items
     * @returns {string[]}
     */
    _getInvestorPlanKeys(investorId, items = []) {
        const groups = items.map(item => ({
            ...item,
            active_subscriptions: this._getGroupSubscriptions(item)
        }));

        return getInvestorPlanKeys(investorId, groups);
    }

    /**
     * Collect the subscriptions an investor currently has across every
     * non-deleted mock group they belong to.
     * @param {string} investorId
     * @param {object[]} items
     * @returns {object[]}
     */
    _getInvestorSubscriptions(investorId, items = []) {
        const groups = items.map(item => ({
            ...item,
            active_subscriptions: this._getGroupSubscriptions(item)
        }));

        return getInvestorSubscriptions(investorId, groups);
    }

    /**
     * Build the next available mock billing group id.
     * @returns {string}
     */
    _buildGroupId() {
        const items = this._getMocked();
        const maxIndex = items.reduce((max, item) => {
            const match = String(item.id || '').match(/^mock-bg-(\d+)$/);

            return match ? Math.max(max, parseInt(match[1], 10)) : max;
        }, 0);

        return `mock-bg-${maxIndex + 1}`;
    }

    /**
     * Build a brand new (empty) billing group item.
     * @param {object} [params={}]
     * @returns {object}
     */
    _buildNewGroup(params = {}) {
        const investorIds = Array.isArray(params.investor_ids) ? [...new Set(params.investor_ids.filter(Boolean))] : [];

        return {
            id: this._buildGroupId(),
            name: String(params.name || '').trim() || 'New billing group',
            investor_id: investorIds[0] || null,
            adviser_id: params.adviser_id || null,
            investors_count: investorIds.length,
            investor_ids: investorIds,
            investors: this._buildInvestors(investorIds),
            active_subscriptions: Array.isArray(params.active_subscriptions) ? params.active_subscriptions : [],
            created_by: this._auth()?.user || null,
            created_by_id: this._auth()?.user?.id || null,
            created_at: BaseApi.clock().toISOString(),
            deleted_at: null
        };
    }

    /**
     * Build a solo billing group for a single investor (used when an investor
     * leaves a group and is not a member of any other group). The solo group
     * carries the plans the investor was on.
     * @param {object} investor
     * @param {string|null} adviserId
     * @param {object[]} [subscriptions=[]]
     * @returns {object}
     */
    _buildSoloGroup(investor = {}, adviserId = null, subscriptions = []) {
        return this._buildNewGroup({
            name: investor.name || 'New billing group',
            adviser_id: adviserId,
            investor_ids: [investor.id],
            active_subscriptions: subscriptions
        });
    }

    /**
     * Remove a set of investors from every other group in the mock store.
     * Groups left with no investors are soft-deleted.
     * @param {object[]} items
     * @param {string} excludedId
     * @param {string[]} movingIds
     * @returns {string[]} ids of groups that were soft-deleted
     */
    _removeInvestorsFromOtherGroups(items, excludedId, movingIds = []) {
        const deletedGroupIds = [];

        if (!movingIds.length) {
            return deletedGroupIds;
        }

        for (let index = 0; index < items.length; index += 1) {
            /* eslint-disable-next-line security/detect-object-injection -- index from loop over our array */
            const item = items[index];

            if (item.id === excludedId || item.deleted_at) {
                continue;
            }

            const currentIds = Array.isArray(item.investor_ids)
                ? item.investor_ids
                : MOCK_SCENARIOS[item._scenario_index]?.investor_ids || [];
            const overlaps = currentIds.filter(id => movingIds.includes(id));

            if (!overlaps.length) {
                continue;
            }

            const remainingIds = currentIds.filter(id => !movingIds.includes(id));
            const updatedItem = {
                ...item,
                investor_ids: remainingIds,
                investor_id: remainingIds[0] || null,
                investors: this._buildInvestors(remainingIds),
                investors_count: remainingIds.length,
                deleted_at: remainingIds.length === 0 ? BaseApi.clock().toISOString() : item.deleted_at || null,
                _scenario_index: undefined
            };

            /* eslint-disable-next-line security/detect-object-injection -- index from loop over our array */
            items[index] = updatedItem;

            if (updatedItem.deleted_at && !item.deleted_at) {
                deletedGroupIds.push(item.id);
            }
        }

        return deletedGroupIds;
    }

    /**
     * Apply a new membership set to a billing group:
     * - updates the group's investors and counts,
     * - removes any newly added investors from their previous groups,
     * - creates solo groups for any removed investors who are not members of
     *   another group,
     * - soft-deletes any group (including this one) that becomes empty.
     * @param {object[]} items
     * @param {number} index
     * @param {string[]} investorIds
     * @param {object} [results]
     * @returns {object}
     */
    _applyMembership(items, index, investorIds = [], results = {}) {
        /* eslint-disable-next-line security/detect-object-injection -- index from findIndex on our array */
        const group = items[index];
        const previousIds = Array.isArray(group.investor_ids) ? group.investor_ids : [];
        const nextIds = [...new Set(investorIds)];
        const removedIds = previousIds.filter(id => !nextIds.includes(id));
        const addedIds = nextIds.filter(id => !previousIds.includes(id));
        const soloGroups = [];

        for (const investorId of removedIds) {
            const investor = MOCK_INVESTORS.find(inv => inv.id === investorId) || {
                id: investorId,
                name: null,
                email: null
            };
            const stillMemberOfGroup = items.some((item, i) => {
                if (i === index || item.deleted_at) {
                    return false;
                }

                return Array.isArray(item.investor_ids) && item.investor_ids.includes(investorId);
            });

            if (!stillMemberOfGroup) {
                soloGroups.push(
                    this._buildSoloGroup(investor, group.adviser_id, this._getInvestorSubscriptions(investorId, items))
                );
            }
        }

        const otherDeletedGroups = addedIds.length
            ? this._removeInvestorsFromOtherGroups(items, group.id, addedIds)
            : [];

        const updated = {
            ...group,
            investor_ids: nextIds,
            investor_id: nextIds[0] || null,
            investors: this._buildInvestors(nextIds),
            investors_count: nextIds.length,
            deleted_at: group.deleted_at || null,
            _scenario_index: undefined
        };

        if (updated.investors_count === 0) {
            updated.deleted_at = BaseApi.clock().toISOString();
            results.deleted = true;
        }

        /* eslint-disable-next-line security/detect-object-injection -- index from findIndex */
        items[index] = updated;

        if (soloGroups.length) {
            items.push(...soloGroups);
            results.solo_groups = soloGroups;
        }

        if (otherDeletedGroups.length) {
            results.other_deleted_groups = otherDeletedGroups;
        }

        return { updated, results };
    }

    // eslint-disable-next-line no-unused-vars
    async index_mock(offset = 0, limit = 100, sort_by = null, filters = {}) {
        await wait(200);

        const normalisedFilters = this._normaliseMockFilters(filters);
        let items = this._getMocked();

        if (!items.length || !this._hasCurrentSeed(items)) {
            items = this._buildScenarioItems(normalisedFilters);
            this._saveMocked(items);
        }

        items = items.filter(item => !item.deleted_at);

        if (Object.keys(normalisedFilters).length) {
            items = this._filterMocked(items, normalisedFilters);
        }

        const sliced = offset !== null && limit !== null ? items.slice(offset, offset + limit) : items;

        const response = {
            status: 'success',
            data: sliced,
            metadata: {
                total: items.length,
                limit,
                offset
            }
        };

        return this._respond(response, () => {
            this._insertModels(response.data);
        });
    }

    async get_mock(id) {
        const items = this._getMocked();
        const data = items.find(item => item.id === id);

        if (!data) {
            return {
                status: 'fail',
                message: 'Not found'
            };
        }

        const enriched = this._enrichDetail(data);
        const response = {
            status: 'success',
            data: enriched
        };

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
            response.data = this._filterGetResponseData(response.data);

            this._updateItemInCollections(id, response.data);
            this._insertModel(response.data);
        });
    }

    async post_mock(data = {}) {
        await wait(150);

        // A billing group takes its rate plans from its founding members, so
        // creation requires at least one investor and every investor must
        // already share the same plan set. Consolidating clients onto one plan
        // is therefore create-then-add: POST with an investor already on the
        // intended plan, then PATCH the full list.
        const investorIds = [...new Set((Array.isArray(data.investor_ids) ? data.investor_ids : []).filter(Boolean))];

        if (!investorIds.length) {
            return {
                status: 'fail',
                message: 'At least one investor is required to create a billing group.'
            };
        }

        const items = this._getMocked();
        const planKeySets = investorIds.map(id => this._getInvestorPlanKeys(id, items));
        const foundingKeys = planKeySets[0] || [];

        if (!planKeySets.every(keys => samePlanKeys(keys, foundingKeys))) {
            return {
                status: 'fail',
                message:
                    'Investors must already share the same rate plans to create a billing group together. ' +
                    'Create the group with an investor already on the intended plan, ' +
                    'then add the rest via Manage members.'
            };
        }

        const subscriptions = Array.isArray(data.active_subscriptions)
            ? data.active_subscriptions
            : this._getInvestorSubscriptions(investorIds[0], items);

        const newGroup = this._buildNewGroup({
            ...data,
            investor_ids: investorIds,
            active_subscriptions: subscriptions
        });

        items.push(newGroup);

        // Any investors that join this group must leave their previous group.
        // Empty departing groups are soft-deleted.
        const deletedGroupIds = this._removeInvestorsFromOtherGroups(items, newGroup.id, newGroup.investor_ids);

        this._saveMocked(items);

        const response = {
            status: 'success',
            data: this._enrichDetail(newGroup),
            meta: {
                deleted_group_ids: deletedGroupIds
            }
        };

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
            response.data = this._filterPostResponseData(response.data);

            this._clearCollection();
            this._insertModel(response.data, this.ApiResponseFormatEnum.DETAIL);
        });
    }

    /**
     * Shared implementation for patch/put updates. Handles name edits and
     * membership changes (add/remove investors) including solo group creation
     * and soft-deletion of empty groups.
     * @param {string|null} id
     * @param {object} data
     * @param {string} method
     * @returns {object}
     */
    async _applyUpdateMock(id, data = {}, method = 'patch') {
        await wait(150);

        const items = this._getMocked();
        const index = items.findIndex(item => item.id === id);

        if (index === -1) {
            return {
                status: 'fail',
                message: 'Billing group not found.'
            };
        }

        const results = {
            solo_groups: [],
            deleted: false
        };

        /* eslint-disable-next-line security/detect-object-injection -- index from findIndex */
        let updated = { ...items[index], ...data };

        if (Array.isArray(data.investor_ids)) {
            const state = this._applyMembership(items, index, data.investor_ids, results);
            updated = state.updated;
        } else {
            /* eslint-disable-next-line security/detect-object-injection -- index from findIndex */
            items[index] = updated;
        }

        this._saveMocked(items);

        const response = {
            status: 'success',
            data: results.deleted ? null : this._enrichDetail(updated),
            meta: {
                deleted_group: results.deleted ? id : null,
                other_deleted_groups: results.other_deleted_groups || [],
                solo_groups: results.solo_groups
            }
        };

        return this._respond(response, () => {
            if (results.deleted) {
                this._removeItemInCollections(id);
                this.model.delete(id);
            } else {
                response.data = this._filterResponseData(response.data);
                response.data =
                    method === 'put'
                        ? this._filterPutResponseData(response.data)
                        : this._filterPatchResponseData(response.data);

                this._updateItemInCollections(id, response.data);
                this._insertModel(response.data, this.ApiResponseFormatEnum.DETAIL);
            }

            for (const deletedGroupId of results.other_deleted_groups || []) {
                this._removeItemInCollections(deletedGroupId);
                this.model.delete(deletedGroupId);
            }

            if (results.solo_groups.length) {
                const soloGroups = results.solo_groups.map(group =>
                    this._filterResponseData(this._enrichDetail(group))
                );

                this._insertModels(soloGroups);
            }
        });
    }

    async patch_mock(id, data = {}) {
        return this._applyUpdateMock(id, data, 'patch');
    }

    async put_mock(data = { id: null }) {
        return this._applyUpdateMock(data.id, data, 'put');
    }
}

export default BillingGroupApi;
